import {FC} from "react";
import {useTranslation} from "react-i18next";
import {
  OrganizationInformation,
  Oem
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon
} from "@interstate/components/Icons";
import {Badge, BadgeVariant} from "@interstate/components/Badge";
import {SpinnerIcon} from "../../../../../icons";
import {
  SimpleTable,
  TableBody,
  TableRow,
  TableCell
} from "@interstate/components/SimpleTable";
import {BadgeBar} from "../../../../../badges";
import {TabColumn} from "../../../../../tab-content";
import {DetailsColumnHeader} from "../../../../overview";
import {TabRow} from "../../../../../tab-content";
import {useNavigation} from "../../../../../navigation";
import {EntitySourceType} from "../../../../EntitySourceType";

export type EntityStatus = "idle" | "in-progress" | "created" | "failed";

export interface EntityDetailsColumnProps {
  transactionId: string;
  organizationInformation: OrganizationInformation;
  oems?: Oem[];
  tenantChoice: "new" | "existing";
  tenantName: string;
  tenantId?: string;
  status?: EntityStatus;
  entityId?: string;
}

const badgeConfigMap: Record<EntityStatus, {variant: BadgeVariant; labelKey: string; labelDefault: string; icon: React.ReactNode}> = {
  "idle": {variant: "success", labelKey: "create-new-entity.entity-states.idle", labelDefault: "To be Created", icon: <BuildingOfficeIcon />},
  "in-progress": {variant: "info", labelKey: "create-new-entity.entity-states.in-progress", labelDefault: "In Progress", icon: <SpinnerIcon />},
  "created": {variant: "success", labelKey: "create-new-entity.entity-states.created", labelDefault: "Created", icon: <CheckCircleIcon />},
  "failed": {variant: "error", labelKey: "create-new-entity.entity-states.failed", labelDefault: "Failed", icon: <XCircleIcon />}
};

export const EntityDetailsColumn: FC<EntityDetailsColumnProps> = ({
  transactionId,
  organizationInformation,
  oems,
  tenantChoice,
  tenantName,
  tenantId,
  status = "idle",
  entityId
}) => {
  const {t} = useTranslation();
  const {navigateToEntity} = useNavigation();

  const tenantDisplay =
    tenantChoice === "new"
      ? t(
          "create-new-entity.review.tenant-new",
          "New tenant: {{tenantName}}",
          {tenantName}
        )
      : t(
          "create-new-entity.review.tenant-existing",
          "Tenant: {{tenantName}} ({{tenantId}})",
          {tenantName, tenantId}
        );

  const badge = badgeConfigMap[status];

  return (
    <TabColumn
      name={"entity-details-column"}
      header={
        <DetailsColumnHeader
          label={t(
            status === "idle"
              ? "create-new-entity.review.entity-details-label"
              : "create-new-entity.results.entity-details-label",
            status === "idle" ? "New Operational Entity Details" : "Organization Details"
          )}
          aside={
            <BadgeBar>
              <div
                style={status === "created" ? {cursor: "pointer"} : undefined}
                onClick={event => {
                  if (status === "created" && entityId) {
                    event.stopPropagation();
                    navigateToEntity(entityId, EntitySourceType.OPERATIONAL);
                  }
                }}>
                <Badge
                  id={`create-new-entity-badge-${transactionId}`}
                  data-testid={`create-new-entity-badge-${transactionId}`}
                  className={"create-new-entity-badge"}
                  variant={badge.variant}
                  startIcon={badge.icon}
                  endIcon={status === "created" ? <ArrowTopRightOnSquareIcon /> : undefined}>
                  <span className={"create-new-entity-badge-label"}>
                    {t(badge.labelKey, badge.labelDefault)}
                  </span>
                </Badge>
              </div>
            </BadgeBar>
          }
        />
      }>
      <TabRow
        name={"business-information"}
        header={
          <h4>{t("entity-detail.dealer.business-information")}</h4>
        }>
        <SimpleTable
          id={"entity-business-information-table"}
          data-testid={"entity-business-information-table"}
          dataDensity={"small"}
          background={"white"}
          className={"business-information-table"}
          bordered={false}
          hover={false}
          striped={false}>
          <TableBody>
            {organizationInformation.legalName && (
              <TableRow>
                <TableCell className={"information-field"}>
                  {t("entity-detail.dealer.legal-name")}
                </TableCell>
                <TableCell
                  id={"entity-information-legal-name"}
                  className={"information-value"}>
                  {organizationInformation.legalName}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={"information-field"}>
                {t("entity-detail.dealer.dba-name")}
              </TableCell>
              <TableCell
                id={"entity-information-dba-name"}
                className={"information-value"}>
                {organizationInformation.dbaName}
              </TableCell>
            </TableRow>
            {organizationInformation.status && (
              <TableRow>
                <TableCell className={"information-field"}>
                  {t("entity-detail.dealer.status.business-status", "Business Status")}
                </TableCell>
                <TableCell
                  id={"entity-information-status"}
                  className={"information-value"}>
                  {organizationInformation.status}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </SimpleTable>
      </TabRow>

      {organizationInformation.address && (
        <TabRow
          name={"address-information"}
          header={
            <h4>{t("entity-detail.dealer.address.details", "Address")}</h4>
          }>
          <SimpleTable
            id={"entity-address-table"}
            data-testid={"entity-address-table"}
            dataDensity={"small"}
            background={"white"}
            bordered={false}
            hover={false}
            striped={false}>
            <TableBody>
              {organizationInformation.address.streetAddressLine1 && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.street", "Street")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.streetAddressLine1}
                  </TableCell>
                </TableRow>
              )}
              {organizationInformation.address.streetAddressLine2 && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.streetAddressLine2", "Suite")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.streetAddressLine2}
                  </TableCell>
                </TableRow>
              )}
              {organizationInformation.address.city && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.city", "City")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.city}
                  </TableCell>
                </TableRow>
              )}
              {organizationInformation.address.state && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.state", "State")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.state}
                  </TableCell>
                </TableRow>
              )}
              {organizationInformation.address.zip && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.zip-code", "Zip")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.zip}
                  </TableCell>
                </TableRow>
              )}
              {organizationInformation.address.country && (
                <TableRow>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.address.country", "Country")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {organizationInformation.address.country}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </SimpleTable>
        </TabRow>
      )}

      {organizationInformation.telecomNumbers && organizationInformation.telecomNumbers.length > 0 && (
        <TabRow
          name={"phone-information"}
          header={
            <h4>{t("entity-detail.dealer.phone", "Phone")}</h4>
          }>
          <SimpleTable
            id={"entity-phone-table"}
            data-testid={"entity-phone-table"}
            dataDensity={"small"}
            background={"white"}
            bordered={false}
            hover={false}
            striped={false}>
            <TableBody>
              {organizationInformation.telecomNumbers.map((phone, idx) => (
                <TableRow key={idx}>
                  <TableCell className={"information-field"}>
                    {t("entity-detail.dealer.phone-number", "Phone")}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {phone}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SimpleTable>
        </TabRow>
      )}

      {oems && oems.length > 0 && (
        <TabRow
          name={"oems-information"}
          header={
            <h4>{t("entity-detail.dealer.oems", "OEMs")}</h4>
          }>
          <SimpleTable
            id={"entity-oems-table"}
            data-testid={"entity-oems-table"}
            dataDensity={"small"}
            background={"white"}
            bordered={false}
            hover={false}
            striped={false}>
            <TableBody>
              {oems.map((oem, idx) => (
                <TableRow key={idx}>
                  <TableCell className={"information-field"}>
                    {oem.abbreviation ?? "OEM"}
                  </TableCell>
                  <TableCell className={"information-value"}>
                    {oem.nameplateDescription ?? oem.id ?? ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SimpleTable>
        </TabRow>
      )}
    </TabColumn>
  );
};
